import { DayPicker, type DateRange, type DropdownProps, type Matcher } from "@daypicker/react";
import { enGB } from "@daypicker/react/locale";
import { Popover } from "@base-ui/react/popover";
import { IconCalendar } from "@tabler/icons-react";
import { useEffect, useId, useRef, useState, type ChangeEvent, type CSSProperties, type KeyboardEvent } from "react";
import { MONTHS } from "./format";
import { Select } from "./select";
import { TimeField } from "./time-field";
import { useFieldLabelId } from "./ui";

/*
 * Date fields: a text field where the date can be typed (dd/mm/yyyy) and a calendar (DayPicker, the
 * calendar library) in a Base UI popover. Values are ISO strings like the native inputs ("2026-12-03",
 * "" for none), so they drop in where `<input type="date">` was.
 */

const pad = (n: number) => String(n).padStart(2, "0");
/** "2026-12-03" → a local Date at midnight (no time-zone shift). */
export function isoToDate(iso: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getMonth() === Number(m[2]) - 1 ? d : undefined;
}
export const dateToIso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const display = (iso: string) => (isoToDate(iso) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");

/** What someone typed → ISO: 3/12/2026, 03-12-26, 3.12.2026 or 2026-12-03; undefined when it is not a date. */
export function parseTypedDate(text: string): string | undefined {
  const t = text.trim();
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  const dmy = /^(\d{1,2})[/.\-\s](\d{1,2})[/.\-\s](\d{2}|\d{4})$/.exec(t);
  const year = dmy?.[3] ?? "";
  const [y, mo, d] = iso ? [Number(iso[1]), Number(iso[2]), Number(iso[3])] : dmy ? [Number(year.length === 2 ? `20${year}` : year), Number(dmy[2]), Number(dmy[1])] : [];
  if (!y || !mo || !d) return undefined;
  const out = `${y}-${pad(mo)}-${pad(d)}`;
  return isoToDate(out) ? out : undefined;
}

/** The calendar's month and year dropdowns, as kit Selects. */
function CalendarDropdown({ options, value, onChange, "aria-label": label }: DropdownProps) {
  return (
    <Select
      aria-label={label ?? "Choose"}
      className="sel-small"
      value={String(value ?? "")}
      onChange={(v) => onChange?.({ target: { value: v } } as unknown as ChangeEvent<HTMLSelectElement>)}
      options={(options ?? []).map((o) => ({ value: String(o.value), label: o.label, disabled: o.disabled }))}
    />
  );
}

interface CalendarBounds { min?: string | undefined; max?: string | undefined }
function calendarProps({ min, max }: CalendarBounds) {
  const year = new Date().getFullYear();
  const lo = min ? isoToDate(min) : undefined;
  const hi = max ? isoToDate(max) : undefined;
  const disabled: Matcher[] = [];
  if (lo) disabled.push({ before: lo });
  if (hi) disabled.push({ after: hi });
  return {
    locale: enGB,
    weekStartsOn: 1 as const,
    captionLayout: "dropdown" as const,
    // Birth dates go far back, departures a few years ahead.
    startMonth: lo ?? new Date(year - 110, 0),
    endMonth: hi ?? new Date(year + 5, 11),
    disabled,
    showOutsideDays: true,
    fixedWeeks: true,
    components: { Dropdown: CalendarDropdown },
    className: "cal-picker",
  };
}

/** A text field for one date that accepts typing and commits on blur or Enter. */
function DateText({ value, onCommit, accept = () => true, placeholder, disabled, required, id, ariaLabel, labelId, style }: {
  value: string; onCommit: (iso: string) => void; accept?: (iso: string) => boolean; placeholder: string; disabled?: boolean | undefined; required?: boolean | undefined;
  id?: string | undefined; ariaLabel?: string | undefined; labelId?: string | undefined; style?: CSSProperties | undefined;
}) {
  const [text, setText] = useState(display(value));
  useEffect(() => setText(display(value)), [value]);
  const commit = () => {
    if (!text.trim()) {
      if (!required && value) onCommit("");
      else setText(display(value));
      return;
    }
    const iso = parseTypedDate(text);
    // Not a date, or outside the allowed dates: back to the last good value.
    if (iso && accept(iso)) {
      if (iso !== value) onCommit(iso);
      setText(display(iso));
    } else setText(display(value));
  };
  return (
    <input
      className="date-text"
      value={text}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      id={id}
      inputMode="numeric"
      autoComplete="off"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabel ? undefined : labelId}
      style={style}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== "Enter") return;
        // Enter takes the typed date, then submits the form as the native date input did (after the
        // new value has reached the form's state).
        e.preventDefault();
        commit();
        const form = e.currentTarget.form;
        if (form) setTimeout(() => form.requestSubmit());
      }}
    />
  );
}

export interface DatePickerProps {
  /** "2026-12-03", or "" for none. */
  value: string;
  onChange: (value: string) => void;
  /** Earliest and latest dates that can be chosen (ISO). */
  min?: string;
  max?: string;
  placeholder?: string;
  disabled?: boolean;
  /** It can't be emptied. */
  required?: boolean;
  /** For forms read with FormData: a hidden input carries the ISO value. */
  name?: string;
  id?: string;
  /** Needed outside a Field. */
  "aria-label"?: string;
  className?: string;
  style?: CSSProperties;
}

/** One date: type it (dd/mm/yyyy) or pick it in the calendar. */
export function DatePicker({ value, onChange, min, max, placeholder = "dd/mm/yyyy", disabled, required, name, id, className, style, ...rest }: DatePickerProps) {
  const labelId = useFieldLabelId();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLDivElement>(null);
  const selected = isoToDate(value);
  const inRange = (iso: string) => (!min || iso >= min) && (!max || iso <= max);
  return (
    <div className={`date-field${className ? ` ${className}` : ""}`} style={style} ref={anchor}>
      {name && <input type="hidden" name={name} value={value} />}
      <DateText value={value} onCommit={onChange} accept={inRange} placeholder={placeholder} disabled={disabled} required={required} id={id} ariaLabel={rest["aria-label"]} labelId={labelId} />
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger className="ghost icon date-button" disabled={disabled} aria-label={selected ? `Change date, ${selected.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}` : "Choose date"}>
          <IconCalendar size={16} />
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Positioner className="pop-positioner" anchor={anchor} side="bottom" align="start" sideOffset={4}>
            <Popover.Popup className="pop">
              <DayPicker
                {...calendarProps({ min, max })}
                mode="single"
                required={required}
                selected={selected}
                defaultMonth={selected ?? (min && isoToDate(min) && min > dateToIso(new Date()) ? isoToDate(min) : undefined)}
                onSelect={(d: Date | undefined) => { onChange(d ? dateToIso(d) : ""); setOpen(false); }}
                autoFocus
              />
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}

export interface DateRangeValue { from: string; to: string }
export interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  /** Names of the two ends for screen readers; default "From" and "Until". */
  labels?: [string, string];
  /** For forms read with FormData: hidden inputs named `names[0]` and `names[1]` carry the two ends. */
  names?: [string, string];
  className?: string;
  style?: CSSProperties;
}

/** A from–until pair (a filter, a stay): both ends can be typed, or picked together in a two-month calendar. */
export function DateRangePicker({ value, onChange, min, max, disabled, labels = ["From", "Until"], names, className, style }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState<DateRange | undefined>();
  const anchor = useRef<HTMLDivElement>(null);
  const from = isoToDate(value.from);
  const to = isoToDate(value.to);
  const inRange = (iso: string) => (!min || iso >= min) && (!max || iso <= max);
  const set = (next: DateRangeValue) => onChange(next.from && next.to && next.from > next.to ? { from: next.to, to: next.from } : next);
  return (
    <div className={`date-field range${className ? ` ${className}` : ""}`} style={style} ref={anchor} role="group" aria-label={`${labels[0]} – ${labels[1]}`}>
      {names && <><input type="hidden" name={names[0]} value={value.from} /><input type="hidden" name={names[1]} value={value.to} /></>}
      <DateText value={value.from} onCommit={(iso) => set({ ...value, from: iso })} accept={inRange} placeholder="dd/mm/yyyy" disabled={disabled} ariaLabel={labels[0]} />
      <span className="date-sep" aria-hidden>–</span>
      <DateText value={value.to} onCommit={(iso) => set({ ...value, to: iso })} accept={inRange} placeholder="dd/mm/yyyy" disabled={disabled} ariaLabel={labels[1]} />
      <Popover.Root open={open} onOpenChange={(o) => { setOpen(o); setPicking(undefined); }}>
        <Popover.Trigger className="ghost icon date-button" disabled={disabled} aria-label="Choose dates"><IconCalendar size={16} /></Popover.Trigger>
        <Popover.Portal>
          <Popover.Positioner className="pop-positioner" anchor={anchor} side="bottom" align="start" sideOffset={4}>
            <Popover.Popup className="pop">
              <DayPicker
                {...calendarProps({ min, max })}
                mode="range"
                numberOfMonths={2}
                selected={picking ?? (from ? { from, to } : undefined)}
                defaultMonth={from}
                onSelect={(r: DateRange | undefined, day: Date) => {
                  // The first click starts a new range; the second ends it and closes the calendar.
                  if (!picking) { setPicking({ from: day, to: undefined }); return; }
                  const range = r?.from && r.to ? r : { from: picking.from ?? day, to: day };
                  set({ from: range.from ? dateToIso(range.from) : "", to: range.to ? dateToIso(range.to) : "" });
                  setPicking(undefined);
                  setOpen(false);
                }}
                autoFocus
              />
              {(value.from || value.to) && (
                <div className="pop-foot"><button type="button" className="ghost" onClick={() => { onChange({ from: "", to: "" }); setOpen(false); }}>Clear dates</button></div>
              )}
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}

/** A date and a time ("2026-12-03T10:30", "" for none), in place of `<input type="datetime-local">`. */
export function DateTimePicker({ value, onChange, min, max, disabled, required, "aria-label": label }: { value: string; onChange: (value: string) => void; min?: string; max?: string; disabled?: boolean; required?: boolean; "aria-label"?: string }) {
  const [date, time] = value ? [value.slice(0, 10), value.slice(11, 16)] : ["", ""];
  // Inside a Field the time part is named "<field label> time".
  const fieldLabel = useFieldLabelId();
  const timeWord = useId();
  return (
    <div className="date-time" role={label ? "group" : undefined} aria-label={label}>
      {!label && fieldLabel && <span id={timeWord} hidden>time</span>}
      <DatePicker value={date} min={min?.slice(0, 10)} max={max?.slice(0, 10)} disabled={disabled} required={required} aria-label={label ? `${label}, date` : undefined}
        onChange={(d) => onChange(d ? `${d}T${time || "09:00"}` : "")} />
      <TimeField value={time} disabled={disabled || !date} required={Boolean(required && date)} aria-label={label ? `${label}, time` : fieldLabel ? undefined : "Time"} aria-labelledby={!label && fieldLabel ? `${fieldLabel} ${timeWord}` : undefined} onChange={(t) => date && onChange(`${date}T${t || "00:00"}`)} style={{ width: 96 }} />
    </div>
  );
}

/** A month ("2026-12", "" for none), in place of `<input type="month">`. */
/** `emptyLabel` adds a choice for no month ("Any month"), which sets the value to "". */
export function MonthPicker({ value, onChange, fromYear, toYear, disabled, emptyLabel, "aria-label": label = "Month" }: { value: string; onChange: (value: string) => void; fromYear?: number; toYear?: number; disabled?: boolean; emptyLabel?: string; "aria-label"?: string }) {
  const now = new Date().getFullYear();
  const [y, m] = value ? [value.slice(0, 4), value.slice(5, 7)] : [String(now), ""];
  const years = Array.from({ length: (toYear ?? now + 2) - (fromYear ?? now - 5) + 1 }, (_, i) => String((fromYear ?? now - 5) + i));
  return (
    <div className="date-time" role="group" aria-label={label}>
      <Select aria-label={`${label}, month`} disabled={disabled} value={m} placeholder="Month" onChange={(mm) => onChange(mm ? `${y}-${mm}` : "")} options={[...(emptyLabel ? [{ value: "", label: emptyLabel }] : []), ...MONTHS.map((name, i) => ({ value: pad(i + 1), label: name }))]} />
      <Select aria-label={`${label}, year`} disabled={disabled} value={y} onChange={(yy) => onChange(`${yy}-${m || "01"}`)} options={years.map((v) => ({ value: v, label: v }))} />
    </div>
  );
}
